import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Tickets, X } from "lucide-react";
import { useTripCardData } from "@/hooks/useTripCardData";
import { Trip } from "@/types/trip";
import { useIsMobile } from "@/hooks/use-mobile";
import TripCardHeader from "./trip-card/TripCardHeader";
import TripCardBadges from "./trip-card/TripCardBadges";
import TripCardSegment from "./trip-card/TripCardSegment";
import TripCardActions from "./trip-card/TripCardActions";
import TripCardDialogs from "./trip-card/TripCardDialogs";
import { useToast } from "@/hooks/use-toast";
import { getFlightStatus } from "@/utils/flightStatus";
import { useState, useEffect } from "react";
import FullScreenTripTimeline from "./trip-card/FullScreenTripTimeline";
import { useTranslation } from "react-i18next";
import { trackTripCardAction } from "@/utils/analytics";
import { useNavigate } from "react-router-dom";
import { FlutterUtils } from "@/hooks/useFlutterBridge";
import Hey from "@/lib/hey-utils";

interface TripCardProps {
  trip: Trip;
  isFocused?: boolean;
  handleRefreshTrips?: () => void;
}

const TripCard = ({
  trip,
  isFocused = false,
  handleRefreshTrips
}: TripCardProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [checkingFlightStatus, setCheckingFlightStatus] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const isFlutterWebapp = FlutterUtils.isFlutterWebapp();
  const {
    isPastTrip,
    nextSegment,
    firstFlight,
    isLoading,
    getDestinationImage,
    getDestinationName,
    getAirportInfo,
    handleShare,
    handleButtonClick,
    showWeather,
    setShowWeather,
    weatherData,
    handleWeatherClick,
    showActivities,
    setShowActivities,
    activitiesData,
    handleActivitiesClick,
    showFood,
    setShowFood,
    foodData,
    handleFoodClick,
    showLounge,
    setShowLounge,
    openLoungeWebview,
    showCurrency,
    setShowCurrency,
    currencyData,
    handleCurrencyClick,
    showTaxi,
    setShowTaxi,
    handleTaxiClick,
    hotelData,
    mainDestinationCode
  } = useTripCardData(trip, isFocused);
  
  useEffect(() => {
    if (!isLoading) {
      const destinationName = getDestinationName();
      
      console.debug("🔄 TripCard.tsx (mobile) useEffect - checking destination:", {
        tripId: trip.id,
        platform: isMobile ? 'Mobile' : 'Desktop',
        oldIdentifiedDestination: trip.identifiedDestination,
        newDestinationName: destinationName,
        currentDestinationImage: trip.destinationImage,
        computationLocation: "TripCard.tsx (mobile component) useEffect"
      });
      
      if (trip.identifiedDestination !== destinationName) {
        // Store the previous image for debugging
        const previousImage = trip.destinationImage;
        
        trip.identifiedDestination = destinationName;
      trip.destinationImage = getDestinationImage();
        
        console.debug("🏙️ TripCard updating identifiedDestination:", {
          tripId: trip.id,
          platform: isMobile ? 'Mobile' : 'Desktop',
          destinationName,
          previousImage,
          newImage: trip.destinationImage,
          imageChanged: previousImage !== trip.destinationImage,
          imageUpdated: !!trip.destinationImage
        });
      } else {
        // Even if the name hasn't changed, ensure we have an image
        if (!trip.destinationImage) {
          const imageUrl = getDestinationImage();
          console.debug("🖼️ TripCard setting missing image:", {
            tripId: trip.id,
            platform: isMobile ? 'Mobile' : 'Desktop',
            destinationName,
            newImage: imageUrl
          });
          trip.destinationImage = imageUrl;
        }
      }
    }
  }, [isLoading, getDestinationName, getDestinationImage, trip, isMobile]);

  const handleCheckFlightStatus = async (flightNumber: string) => {
    try {
      setCheckingFlightStatus(true);
      
      const flightElement = trip.elements
        .find(element => 
          element.type === 'flight' && 
          (element.details as any).flightNumber === flightNumber
        );
      
      if (!flightElement) {
        toast({
          variant: "destructive",
          description: t("tripCard.noFlightInfoFound", "No flight information found")
        });
        return;
      }
      
      const flight = flightElement.details as any;
      const date = new Date(flight.departure.time);
      const formattedDate = date.toISOString().split('T')[0];
      const formattedFlightId = `${flightNumber}_${flight.departure.code}_${formattedDate}`;
      console.debug('🌐 Flight IDs:', [formattedFlightId]);
      console.debug("call from Trip card:::::::>>>>>", formattedFlightId);
      
      const statusData = await getFlightStatus([formattedFlightId]);
      
      if (statusData.length === 0) {
        return;
      }
    } catch (error) {
      console.error("Error checking flight status:", error);
      toast({
        variant: "destructive",
        description: t("tripCard.failedFlightStatus", "Failed to check flight status. Please try again.")
      });
    } finally {
      setCheckingFlightStatus(false);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    console.debug("🔥 COUCOU Trip card clicked:", trip.id);
    handleRefreshTrips();
    if (isMobile) {
      if (e) {
        e.stopPropagation();
      }
      const destinationName = getDestinationName();
      trackTripCardAction("view_timeline", trip.id, destinationName);
      setIsFullScreen(true);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-travel-500" />
      </div>;
  }
  
  const flightDepartureCode = firstFlight ? (firstFlight.details as any).departure.code : null;

  // ALWAYS retrieve the destination name fresh by calling the function
  const destinationName = getDestinationName();
  const destinationImage = getDestinationImage();
  
  if (trip.bookingReference) {
    console.debug(`📋 Displaying booking reference for trip ${trip.id}: ${trip.bookingReference}`);
  }
  
  console.debug("🏙️ TripCard render phase DEBUG:", {
    tripId: trip.id,
    platform: isMobile ? 'Mobile' : 'Desktop',
    mainDestinationCode,
    mappedCityName: destinationName,
    getDestinationNameResult: getDestinationName(),
    getDestinationImageResult: destinationImage,
    tripCurrentDestinationImage: trip.destinationImage,
    areImagesSame: destinationImage === trip.destinationImage,
    finalCardTitle: destinationName,
    identifiedDestination: trip.identifiedDestination,
    computationLocation: "TripCard.tsx (mobile) render phase",
    bookingReference: trip.bookingReference,
    origin: trip.origin
  });

  
  async function handleTransferClick() {
    let firstFlight = trip.elements.find(element => element.type === 'flight');
    if (!firstFlight) {
      console.error("No flight found for trip:", trip.id);
      toast({
        variant: "destructive",
        description: t("tripCard.noFlightForTransfer", "No flight information available for transfer")
      });
      return;
    }
    let flightIdentifier = (firstFlight.details as any).flightNumber;
    let carrierCode = flightIdentifier.substring(0, 2).toUpperCase(); // Assuming flightIdentifier is in the format "AA1234" or similar
    let flightNumber = flightIdentifier.substring(2); // Extract the flight number part
    let date = new Date((firstFlight.details as any).departure.time);
    let formattedDate = date.toISOString().split('T')[0];

    try {
      let contextualisationTransfers = await Hey.HeyUtils.getInstance().getActivities(trip.id, {
        nbResults: 5,
        date : "2025-10-21",
        carrierCode: carrierCode,
        flightIdentifier: flightNumber
      }, "transferLocationToAirport");
      if (contextualisationTransfers?.results?.length > 0 && contextualisationTransfers?.results[0]?.url?.length > 0) {
        // open new tab with url
        console.debug("🚖 Transfer URL found:", contextualisationTransfers.results[0].url);
        window.open(contextualisationTransfers.results[0].url, '_blank');
      }
      else {
        console.warn("No transfer URL found for trip:", trip.id);
        toast({
          variant: "destructive",
          description: t("tripCard.noTransferData", "No transfer data available for this trip")
        });
      }
    }
    catch (error) {
      console.error("Error fetching transfer data:", error);
      toast({
        variant: "destructive",
        description: t("tripCard.failedTransferData", "No transfer data found")
      });
      return;
    }
  }

  async function handleTripCardRelatedNotificationActions() {
    let postAction = await FlutterUtils.getCurrenttNotifPostAction();
    if (postAction && postAction?.trip == trip?.id) {
      if (postAction?.type == 'activity') {
        console.log("offer data: ", postAction.offerData);
        handleCardClick(null);
        handleActivitiesClick();
        console.log("clicked on activity");
      }
      else if (postAction?.type == 'restaurants') {
        handleFoodClick();
      }
      else if (postAction?.type == 'weather') {
        handleWeatherClick();
      }
      else if (postAction?.type == 'lounge') {
        openLoungeWebview();
      }
      else if (postAction?.type == 'airobot') {
        console.log("AI Robot action triggered");
      }
      else {
        console.error("No action to execute for this post action");
      }
      FlutterUtils.clearPostAction();
    }
  }

  handleTripCardRelatedNotificationActions();

  return (
    <div className="relative">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ 
          opacity: isFullScreen ? 0 : 1, 
          y: isFullScreen ? 20 : 0,
          scale: isFullScreen ? 0.95 : 1,
          transition: {
            duration: 0.3
          }
        }}
        className={`w-full ${isMobile ? 'max-w-[330px]' : 'max-w-full md:max-w-none'} mx-auto rounded-xl overflow-hidden bg-white/80 backdrop-blur-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${isMobile ? 'cursor-pointer' : ''}`}
        onClick={handleCardClick}
        whileTap={isMobile ? { scale: 0.98 } : undefined}
        style={{ 
          position: "relative",
          zIndex: isFullScreen ? 0 : 10,
          pointerEvents: isFullScreen ? "none" : "auto"
        }}
      >
        <div className={`flex ${isMobile ? 'flex-col' : 'md:flex-row'}`}>
          <div className={`${isMobile ? 'w-full' : 'md:w-1/4'}`}>
            <TripCardHeader 
              destinationImage={destinationImage} 
              destinationName={destinationName} 
              remainingDays={trip.remainingDays} 
              isFocused={isFocused} 
              onShare={handleShare} 
              bookingReference={trip.bookingReference} 
            />
          </div>
          
          <div className={`p-5 space-y-3 px-[20px] py-[12px] ${isMobile ? '' : 'md:flex-1'}`}>
            <div className="py-0 px-[15px]">
              <h2 className="text-xl font-bold truncate text-travel-700" title={destinationName}>
                {destinationName}
              </h2>
              <div className="flex flex-col gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-travel-500" />
                  <span className="text-travel-500 text-sm font-medium">{trip.dates}</span>
                </div>
                <TripCardBadges tripType={trip.tripType} numberOfTravelers={trip.numberOfTravelers} />
              </div>
            </div>

            <div className="space-y-3">
              <TripCardSegment 
                isPastTrip={isPastTrip} 
                nextSegment={nextSegment} 
                getAirportInfo={getAirportInfo} 
                handleCheckFlightStatus={handleCheckFlightStatus} 
                tripId={trip.id} 
                destinationName={destinationName}
              />
            </div>
          </div>
          
          {!isPastTrip && !isMobile && (
            <div className="hidden md:flex md:w-1/6 md:flex-col md:justify-center md:items-center md:pr-4">
              <TripCardActions 
                onWeatherClick={handleWeatherClick} 
                onActivitiesClick={handleActivitiesClick} 
                onFoodClick={handleFoodClick}
                onTransferClick={handleTransferClick}
                handleButtonClick={handleButtonClick}
                tripId={trip.id}
                destinationName={destinationName}
                layout="vertical"
              />
            </div>
          )}
          
          {!isPastTrip && isMobile && (
            <div className="px-[20px] py-[12px]">
              <TripCardActions 
                onWeatherClick={handleWeatherClick} 
                onActivitiesClick={handleActivitiesClick} 
                onFoodClick={handleFoodClick} 
                onTransferClick={handleTransferClick}
                handleButtonClick={handleButtonClick}
                tripId={trip.id}
                destinationName={destinationName}
              />
            </div>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {isFullScreen && isMobile && (
          <motion.div 
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ 
              opacity: 0, 
              y: "100%",
              transition: { 
                type: "spring", 
                damping: 25, 
                stiffness: 300 
              }
            }}
            transition={{ 
              type: "spring", 
              damping: 25, 
              stiffness: 300 
            }}
            className="fixed inset-0 z-[100] bg-white overflow-auto pb-16">
            <div className="sticky top-0 z-10 flex justify-between items-center p-4 bg-white/80 backdrop-blur-md shadow-sm">
              <h2 className="text-xl font-bold text-travel-700">{destinationName}</h2>
              <button 
                onClick={() => setIsFullScreen(false)}
                className="rounded-full p-2 hover:bg-gray-100 text-travel-500 active:bg-travel-50 transition-colors duration-200"
                aria-label="Close"
                >
                <X className="w-5 h-5" />
              </button>
            </div>

            {isMobile && isFullScreen && (
              <div className="px-[20px] py-[12px]">
                <div className="relative mb-6 overflow-hidden rounded-lg shadow-md w-full h-48">
                    <img 
                      src={trip.destinationImage} 
                      alt={destinationName} 
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png";
                      }}
                    />
                    {trip.bookingReference && (
                      <div className="absolute bottom-3 left-3 bg-black/30 backdrop-blur-md rounded-full px-3 py-1 z-10">
                        <span className="text-white text-xs font-medium flex items-center cursor-pointer"
                        onClick={() => {
                          navigator.clipboard.writeText(trip.bookingReference);
                          toast({
                            description: t("tripCard.bookingReferenceCopied", "Booking reference copied to clipboard"),
                            duration: 1000,
                          });
                        }}>
                          
                            <Tickets className="w-4 h-4 mr-1.5 " />
                            {trip.bookingReference}
                        </span>
                      </div>
                    )}
                </div>
                
                <TripCardActions
                  onWeatherClick={handleWeatherClick}
                  onActivitiesClick={handleActivitiesClick}
                  onFoodClick={handleFoodClick}
                  onTransferClick={handleTransferClick}
                  handleButtonClick={handleButtonClick}
                  tripId={trip.id}
                  destinationName={destinationName}
                  layout="horizontal"
                />
              </div>
            )}

            <FullScreenTripTimeline 
              trip={trip} 
              getAirportInfo={getAirportInfo}
              onTransferClick={handleTransferClick}
              handleCheckFlightStatus={handleCheckFlightStatus}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <TripCardDialogs 
        destinationName={destinationName} 
        showWeather={showWeather} 
        setShowWeather={setShowWeather} 
        weatherData={weatherData} 
        showActivities={showActivities} 
        setShowActivities={setShowActivities} 
        activitiesData={activitiesData} 
        showFood={showFood} 
        setShowFood={setShowFood} 
        foodData={foodData} 
        showLounge={showLounge} 
        setShowLounge={setShowLounge} 
        flightDepartureCode={flightDepartureCode} 
        showCurrency={showCurrency} 
        setShowCurrency={setShowCurrency} 
        currencyData={currencyData} 
        showTaxi={showTaxi} 
        setShowTaxi={setShowTaxi} 
        trip={trip} 
      />
    </div>
  );
};

export default TripCard;
